import { Fragment, useEffect, useRef, useState } from 'react'
import { useLang } from '../lib/useLang'
import './project-journey.css'

const STAGE_MS = 5200

function Tick({ style }) {
  return (
    <span className='pj-tick' style={style}>
      <svg width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='3' strokeLinecap='round' strokeLinejoin='round'>
        <path d='M20 6L9 17l-5-5' />
      </svg>
    </span>
  )
}

function useCountUp(target, delayMs = 0, durationMs = 1300) {
  const [value, setValue] = useState(0)
  useEffect(() => {
    let raf, start
    const timer = setTimeout(() => {
      const tick = (ts) => {
        if (!start) start = ts
        const p = Math.min(1, (ts - start) / durationMs)
        setValue(target * (1 - Math.pow(1 - p, 3)))
        if (p < 1) raf = requestAnimationFrame(tick)
      }
      raf = requestAnimationFrame(tick)
    }, delayMs)
    return () => { clearTimeout(timer); raf && cancelAnimationFrame(raf) }
  }, [target, delayMs, durationMs])
  return value
}

/* 01 — goal / data / metric checklist */
function VisualDiscovery({ L }) {
  const rows = [L.goal, L.data, L.metric]
  return (
    <div className='pj-disc'>
      {rows.map((name, i) => (
        <div key={i} className='pj-disc-row' style={{ animationDelay: `${i * 0.22}s` }}>
          <span className='mono'>/0{i + 1}</span>
          <span className='pj-disc-row-name'>{name}</span>
          <Tick style={{ animationDelay: `${i * 0.22 + 0.3}s` }} />
        </div>
      ))}
    </div>
  )
}

/* 02 — training pipeline + quality and money meters */
function VisualTraining({ L }) {
  const steps = Array.isArray(L.pipeline) ? L.pipeline : []
  const q = useCountUp(0.91, 1500)
  const imp = L.impact || {}
  const money = useCountUp(imp.value || 0, 2100)
  return (
    <div className='pj-train'>
      <div className='pj-pipe'>
        {steps.map((name, i) => (
          <Fragment key={i}>
            {i > 0 && (
              <span className='pj-pipe-link'>
                <span style={{ animationDelay: `${i * 0.4 - 0.15}s` }} />
              </span>
            )}
            <span className='pj-pipe-node'>
              <span className='pj-pipe-dot' style={{ animationDelay: `${i * 0.4}s` }} />
              <span className='pj-pipe-name'>{name}</span>
            </span>
          </Fragment>
        ))}
      </div>
      <div className='pj-quality'>
        <div className='pj-quality-head'>
          <span className='pj-quality-name'>{L.quality}</span>
          <span className='pj-quality-val mono'>AUC {q.toFixed(2)}</span>
        </div>
        <div className='pj-quality-track'>
          <span className='pj-quality-fill' />
        </div>
      </div>
      <div className='pj-quality pj-quality--money'>
        <div className='pj-quality-head'>
          <span className='pj-quality-name'>{imp.label}</span>
          <span className='pj-quality-val mono'>
            {imp.prefix}{money.toFixed(imp.decimals ?? 0)}{imp.suffix}
          </span>
        </div>
        <div className='pj-quality-track'>
          <span className='pj-quality-fill pj-quality-fill--money' />
        </div>
      </div>
    </div>
  )
}

/* 03 — decision branch */
function VisualDecision({ L }) {
  return (
    <div className='pj-branch'>
      <div className='pj-branch-layout'>
        <span className='pj-chip'>{L.branchQ}</span>
        <svg className='pj-branch-svg' viewBox='0 0 120 140' aria-hidden='true'>
          <path className='pj-branch-path pj-branch-path--yes' d='M6 70 C 45 70, 60 30, 112 30' />
          <path className='pj-branch-path pj-branch-path--no' d='M6 70 C 45 70, 60 110, 112 110' />
        </svg>
        <span className='pj-branch-ends'>
          <span className='pj-chip pj-chip--yes' style={{ animationDelay: '0.75s' }}>
            <Tick /> {L.branchYes}
          </span>
          <span className='pj-chip pj-chip--no' style={{ animationDelay: '0.9s' }}>
            {L.branchNo}
          </span>
        </span>
      </div>
    </div>
  )
}

/* 04 — production: interactive integration hub + monitor */
function VisualProduction({ L, onInteract }) {
  const systems = Array.isArray(L.systems) ? L.systems : []
  const [connected, setConnected] = useState([])
  const [active, setActive] = useState(null)
  const [userPick, setUserPick] = useState(false)

  // Auto-connect systems one by one on mount
  useEffect(() => {
    const timers = systems.map((_, i) =>
      setTimeout(() => setConnected((c) => (c.includes(i) ? c : [...c, i])), 350 + i * 500)
    )
    return () => timers.forEach(clearTimeout)
  }, [systems.length])

  // Once connected, cycle the highlight through systems until the user picks one
  useEffect(() => {
    if (userPick || systems.length === 0) return
    let interval
    const start = setTimeout(() => {
      setActive(0)
      let i = 0
      interval = setInterval(() => {
        i = (i + 1) % systems.length
        setActive(i)
      }, 2400)
    }, 350 + systems.length * 500)
    return () => {
      clearTimeout(start)
      interval && clearInterval(interval)
    }
  }, [userPick, systems.length])

  const rowGap = 40
  const H = rowGap * systems.length + 4
  const mid = H / 2
  const activeSys = active != null ? systems[active] : null

  return (
    <div className='pj-prod'>
      <div className='pj-prodhub'>
        <span className='pj-chip pj-chip--model'>{L.model}</span>
        <svg className='pj-prodhub-svg' viewBox={`0 0 90 ${H}`} aria-hidden='true'>
          {systems.map((_, i) => {
            const y = rowGap / 2 + i * rowGap
            return (
              <path
                key={active === i ? `r${i}` : i}
                className={`pj-branch-path ${active === i ? 'pj-branch-path--yes' : ''}`}
                style={{ animationDelay: active === i ? '0s' : `${0.3 + i * 0.5}s` }}
                d={`M2 ${mid} C 40 ${mid}, 45 ${y}, 88 ${y}`}
              />
            )
          })}
        </svg>
        <span className='pj-prodhub-ends'>
          {systems.map((s, i) => (
            <button
              key={i}
              type='button'
              className={`pj-chip pj-chip--sys ${connected.includes(i) ? 'pj-chip--yes' : ''} ${active === i ? 'is-focus' : ''}`}
              style={{ animationDelay: `${0.2 + i * 0.5}s` }}
              onClick={() => {
                setConnected((c) => (c.includes(i) ? c : [...c, i]))
                setActive(i)
                setUserPick(true)
                onInteract && onInteract()
              }}
              title={L.connected}
            >
              {connected.includes(i) && <Tick />} {s.name}
            </button>
          ))}
        </span>
      </div>
      <div className='pj-prodhub-desc' key={active ?? 'none'}>
        {activeSys ? `${activeSys.name} — ${activeSys.desc}` : ' '}
      </div>
      <div className='pj-prod-chips'>
        <span className='pj-live'>
          <span className='pj-live-dot' /> {L.monitor}
        </span>
        <span className='pj-chip pj-chip--yes' style={{ animationDelay: '2.4s' }}>{L.drift}</span>
        <span className='pj-chip' style={{ animationDelay: '2.6s' }}>{L.update}</span>
      </div>
    </div>
  )
}

const VISUALS = [VisualDiscovery, VisualTraining, VisualDecision, VisualProduction]

export default function ProjectJourney() {
  const { t } = useLang()
  const stages = t('lp.journey.stages')
  const list = Array.isArray(stages) ? stages : []
  const labels = t('lp.journey.labels') || {}
  const [active, setActive] = useState(0)
  const [paused, setPaused] = useState(false)
  const [inView, setInView] = useState(false)
  const widgetRef = useRef(null)

  useEffect(() => {
    const el = widgetRef.current
    if (!el) return
    const obs = new IntersectionObserver(
      ([e]) => setInView(e.isIntersecting),
      { threshold: 0.35 }
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [])

  useEffect(() => {
    if (paused || !inView || list.length === 0) return
    const id = setInterval(
      () => setActive((a) => (a + 1) % list.length),
      STAGE_MS
    )
    return () => clearInterval(id)
  }, [paused, inView, list.length])

  const select = (i) => {
    setActive(i)
    setPaused(true)
  }

  const stage = list[active] || {}
  const Visual = VISUALS[active] || VisualDiscovery

  return (
    <section id='journey' className='hairline-bottom'>
      <div className='mx-auto max-w-6xl px-6 lg:px-10 py-24 md:py-32'>
        <div data-reveal className='label-eyebrow mb-10'>{t('lp.journey.eyebrow')}</div>

        <h2 data-reveal className='headline-display text-3xl md:text-5xl lg:text-[3.4rem] max-w-3xl'>
          {t('lp.journey.titlePart1')}
          <br />
          <span className='text-muted'>{t('lp.journey.titlePart2')}</span>
        </h2>

        <p data-reveal className='mt-5 text-[0.9rem] text-muted'>{t('lp.journey.hint')}</p>

        <div data-reveal ref={widgetRef} className='pj mt-10 md:mt-14'>
          <div className='pj-tabs' role='tablist'>
            {list.map((s, i) => (
              <button
                key={s.num}
                type='button'
                role='tab'
                aria-selected={i === active}
                className={`pj-tab ${i === active ? 'is-active' : ''} ${i < active ? 'is-done' : ''}`}
                onClick={() => select(i)}
              >
                <span className='pj-tab-num mono'>{s.num}</span>
                <span className='pj-tab-label'>{s.short}</span>
                <span className='pj-tab-track'>
                  {i === active && (
                    <span
                      key={`${active}-${paused}-${inView}`}
                      className={`pj-tab-fill ${!paused && inView ? 'is-running' : 'is-full'}`}
                      style={{ animationDuration: `${STAGE_MS}ms` }}
                    />
                  )}
                </span>
              </button>
            ))}
          </div>

          <div className='pj-body'>
            <div className='pj-detail' key={`d${active}`}>
              <h3 className='pj-detail-title'>{stage.title}</h3>
              <p className='pj-detail-desc'>{stage.desc}</p>
              <ul className='pj-detail-bullets'>
                {(stage.bullets || []).map((b, i) => (
                  <li key={i} style={{ animationDelay: `${0.15 + i * 0.1}s` }}>
                    <svg width='13' height='13' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
                      <path d='M20 6L9 17l-5-5' />
                    </svg>
                    {b}
                  </li>
                ))}
              </ul>
            </div>
            <div className='pj-visual' key={`v${active}`}>
              <Visual L={labels} onInteract={() => setPaused(true)} />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
